import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-6 pt-28 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center cursor-pointer" onClick={() => navigate("/")}>
              <Shield className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold cursor-pointer" onClick={() => navigate("/")}>JepTags</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-blue-200 hover:text-white mb-4 text-sm transition">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-4xl font-bold mb-3">Privacy Policy</h1>
          <p className="text-blue-100">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-12 shadow-sm space-y-8">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              JepTags ("we," "our," or "us") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our product authentication platform, website, mobile applications, and related services (collectively, the "Services").
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-3">We collect information in the following ways:</p>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Account Information</h3>
                <p className="text-gray-600 text-sm leading-relaxed">When you register for a business account, we collect your name, email address, company name, phone number, and business details.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Scan Data</h3>
                <p className="text-gray-600 text-sm leading-relaxed">When products are scanned, we collect scan location (GPS coordinates), device information, timestamp, and tag identification data to verify authenticity and provide analytics.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Usage Data</h3>
                <p className="text-gray-600 text-sm leading-relaxed">We automatically collect information about how you interact with our Services, including IP address, browser type, pages visited, and feature usage.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed">
              We use collected information to provide and maintain our product authentication services, verify product authenticity, generate analytics and reports for business clients, improve our platform and user experience, communicate with you about your account and services, comply with legal obligations, and detect and prevent fraud or unauthorized access.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing</h2>
            <p className="text-gray-600 leading-relaxed">
              We do not sell your personal data to third parties. We may share information with service providers who assist in operating our platform, business clients (aggregated scan analytics only), law enforcement when required by law, and in connection with a merger, acquisition, or sale of assets with appropriate notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement industry-standard security measures including end-to-end encryption, secure data storage with SOC 2 certified infrastructure, regular security audits and penetration testing, and strict access controls and authentication protocols. While we strive to protect your information, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide services. Scan data is retained for the duration of the business client's subscription plus 12 months. You may request deletion of your data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed">
              Depending on your jurisdiction, you may have the right to access, correct, or delete your personal data, object to or restrict processing, data portability, and withdraw consent. To exercise these rights, contact us at privacy@jeptags.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. International Transfers</h2>
            <p className="text-gray-600 leading-relaxed">
              JepTags operates globally with offices in the USA and Vietnam. Your data may be transferred to and processed in countries outside your own. We ensure appropriate safeguards are in place for such transfers in compliance with GDPR and other applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              For questions about this Privacy Policy, contact us at:
            </p>
            <div className="bg-blue-50 rounded-lg p-4 mt-3">
              <p className="text-gray-800 font-medium">JepTags, Inc.</p>
              <p className="text-gray-600 text-sm">2635 Cunningham Ave, California, CA 95148</p>
              <p className="text-gray-600 text-sm">Email: privacy@jeptags.com</p>
              <p className="text-gray-600 text-sm">Phone: +1-844-737-4799</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
